import React from 'react';
import {
  FaObjectGroup,
  FaArrowRight,
  FaDrawPolygon,
  FaImage,
  FaLink,
  FaIcons,
  FaMapMarkerAlt,
  FaPen,
  FaShapes,
  FaTh,
} from 'react-icons/fa';
import { MdStraighten } from 'react-icons/md';
import { renderIconBySlug } from '../../../content/icons.js';
import { Checkbox } from '../../form/checkbox.js';
import { IconPicker } from '../../icons/icon.Picker.js';
import {
  BOARD_TOOLBOX_BASE_BTN_CLASS,
  BoardToolboxActionButton,
} from '../boardToolboxActionButton.js';
import {
  BoardToolboxBar,
  BoardToolboxOverlay,
  BoardToolboxSection,
} from '../boardToolboxBar.js';
import { BoardToolboxBorderStyle } from '../boardToolboxBorderStyle.js';
import { BoardToolboxColorPalette } from '../boardToolboxColorPalette.js';
import { BoardToolboxModeToggle } from '../boardToolboxModeToggle.js';
import { BoardToolboxSelect } from '../boardToolboxSelect.js';
import { BoardToolboxToolList } from '../boardToolboxToolList.js';
import { BoardToolboxZoomControls } from '../boardToolboxZoomControls.js';

const MAP_PIN_SIZE_OPTIONS = [
  { value: 'sm', label: 'Petit' },
  { value: 'md', label: 'Moyen' },
  { value: 'lg', label: 'Grand' },
];

const MAP_SHAPE_TYPE_OPTIONS = [
  { value: 'circle', label: 'Rond' },
  { value: 'rect', label: 'Rectangle' },
  { value: 'diamond', label: 'Losange' },
  { value: 'triangle', label: 'Triangle' },
  { value: 'star', label: 'Etoile' },
];

const MAP_ARROW_OPTIONS = [
  { value: 'none', label: '— Aucune' },
  { value: 'from', label: '← Début' },
  { value: 'to', label: '→ Fin' },
  { value: 'both', label: '↔ Début et fin' },
];

const MAP_TOOL_OPTIONS = [
  {
    tool: 'line',
    title: 'trait entre deux éléments',
    icon: <FaArrowRight size={13} />,
  },
  {
    tool: 'pin',
    title: 'outil pin',
    icon: <FaMapMarkerAlt size={13} />,
  },
  {
    tool: 'region',
    title: 'outil région',
    icon: <FaDrawPolygon size={13} />,
  },
  {
    tool: 'shape',
    title: 'dessiner une forme',
    icon: <FaShapes size={13} />,
  },
  {
    tool: 'measure',
    title: 'mesure de distance',
    icon: <MdStraighten size={14} />,
  },
  {
    tool: 'draw',
    title: 'dessin rapide',
    icon: <FaPen size={12} />,
  },
];

export const BoardToolBoxMap = ({
  zoom = 1,
  leftInset = '1em',
  rightInset = '1em',
  showOnlyZoom = false,
  showMeasureInReadOnly = false,
  allowMeasureSettings = true,
  mapTool = 'select',
  mapShapeType = 'circle',
  interactionMode = 'pan',
  measureMode = 'measure',
  measureScales = [],
  activeMeasureScaleIds = [],
  mapGridVisible = false,
  showColorPalette = false,
  showArrowOptions = false,
  selectedColor = 'secondary',
  selectedPinSize = 'md',
  showShapeColorOptions = false,
  showShapeLayerOptions = false,
  showBorderStyleOptions = false,
  selectedBorderStyle = 'solid',
  showLinkItem = false,
  showUnlinkItem = false,
  showIconPicker = false,
  selectedPinIconSlug = null,
  linkArrows = 'none',
  onColorChange,
  onPinSizeChange,
  onPinIconChange,
  onLinkArrowsChange,
  onUnlinkItem,
  onOpenLinkModal,
  onBorderStyleChange,
  onLayerChange,
  onInteractionModeChange,
  onMapToolChange,
  onMeasureModeChange,
  onToggleMeasureScale,
  onMapShapeTypeChange,
  onOpenBackgroundModal,
  onOpenGridSettingsModal,
  onToggleGridVisible,
  onZoomOut,
  onZoomReset,
  onZoomIn,
  showEnterGroup = false,
  onEnterGroup,
  showUngroup = false,
  onUngroup,
  showExitGroup = false,
  onExitGroup,
  activeGroupLabel = '',
}) => {
  const [iconPickerOpen, setIconPickerOpen] = React.useState(false);
  const handleMeasureToolContextMenu = event => {
    if (!allowMeasureSettings) {
      return;
    }
    event.preventDefault();
    onMapToolChange?.('measure');
    onMeasureModeChange?.('settings');
  };
  const handleMapToolChange = nextTool => {
    onMapToolChange?.(nextTool);
    if (nextTool === 'measure') {
      onMeasureModeChange?.('measure');
    }
  };

  const measureTooltipContent = (
    <div className='flex flex-col gap-03'>
      <div className='text-xs'>mesure de distance</div>
      {measureScales?.length > 0 && (
        <div className='mt-03 border-t border-contrast border-opacity-20 pt-03'>
          <div className='mb-03 text-xs opacity-80'>Echelles actives</div>
          <div className='flex flex-col gap-03'>
            {measureScales.map(scale => {
              const isActive = activeMeasureScaleIds.includes(scale?.id);
              const unit = String(scale?.unit || '').trim() || '—';
              return (
                <label
                  key={scale?.id}
                  className='flex items-center gap-03 text-xs cursor-pointer'
                  onClick={event => event.stopPropagation()}
                >
                  <Checkbox
                    size='sm'
                    value={isActive}
                    onChange={() => onToggleMeasureScale?.(scale?.id)}
                    onClick={event => event.stopPropagation()}
                  />
                  <span className='truncate'>
                    {scale?.value} {unit}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );

  const mapToolOptions = MAP_TOOL_OPTIONS.map(option =>
    option.tool === 'measure'
      ? {
          ...option,
          title: measureTooltipContent,
          onContextMenu: handleMeasureToolContextMenu,
        }
      : option,
  );

  if (showOnlyZoom && !showMeasureInReadOnly) {
    return (
      <BoardToolboxBar leftInset={leftInset}>
        <BoardToolboxZoomControls
          zoom={zoom}
          onZoomOut={onZoomOut}
          onZoomReset={onZoomReset}
          onZoomIn={onZoomIn}
          baseBtnClass={BOARD_TOOLBOX_BASE_BTN_CLASS}
        />
      </BoardToolboxBar>
    );
  }

  if (showOnlyZoom && showMeasureInReadOnly) {
    const hasRecordedMeasureScale = Array.isArray(measureScales) && measureScales.length > 0;
    return (
      <BoardToolboxBar leftInset={leftInset}>
        {hasRecordedMeasureScale && (
          <BoardToolboxToolList
            activeTool={mapTool}
            onToolChange={handleMapToolChange}
            tools={mapToolOptions.filter(option => option.tool === 'measure')}
          />
        )}
        <BoardToolboxZoomControls
          zoom={zoom}
          onZoomOut={onZoomOut}
          onZoomReset={onZoomReset}
          onZoomIn={onZoomIn}
          baseBtnClass={BOARD_TOOLBOX_BASE_BTN_CLASS}
        />
      </BoardToolboxBar>
    );
  }

  const hasContextOptions =
    showColorPalette ||
    showArrowOptions ||
    showIconPicker ||
    showLinkItem ||
    showUnlinkItem ||
    showBorderStyleOptions ||
    mapTool === 'shape' ||
    showShapeColorOptions;

  return (
    <>
      <BoardToolboxBar leftInset={leftInset}>
        <BoardToolboxModeToggle
          interactionMode={interactionMode}
          onInteractionModeChange={onInteractionModeChange}
          baseBtnClass={BOARD_TOOLBOX_BASE_BTN_CLASS}
        />
        <BoardToolboxToolList
          activeTool={mapTool}
          onToolChange={handleMapToolChange}
          tools={mapToolOptions}
        />
        {showEnterGroup && (
          <BoardToolboxActionButton
            title='entrer dans le groupe'
            onClick={() => onEnterGroup?.()}
          >
            ↳
          </BoardToolboxActionButton>
        )}
        {showUngroup && (
          <BoardToolboxActionButton
            title='éclater le groupe'
            onClick={() => onUngroup?.()}
          >
            <FaObjectGroup size={13} />
            <span className='text-xs ml-05'>×</span>
          </BoardToolboxActionButton>
        )}
        {showExitGroup && (
          <BoardToolboxActionButton
            title={
              activeGroupLabel
                ? `retour: ${activeGroupLabel}`
                : 'retour au board'
            }
            active={true}
            onClick={() => onExitGroup?.()}
          >
            ↰
          </BoardToolboxActionButton>
        )}
        <BoardToolboxActionButton
          title='image de fond'
          onClick={() => onOpenBackgroundModal?.()}
        >
          <FaImage size={12} />
        </BoardToolboxActionButton>
        <BoardToolboxActionButton
          title='grille (clic droit: réglages)'
          active={mapGridVisible}
          onClick={() => onToggleGridVisible?.()}
          onContextMenu={event => {
            event.preventDefault();
            onOpenGridSettingsModal?.();
          }}
        >
          <FaTh size={12} />
        </BoardToolboxActionButton>
        <BoardToolboxZoomControls
          zoom={zoom}
          onZoomOut={onZoomOut}
          onZoomReset={onZoomReset}
          onZoomIn={onZoomIn}
          baseBtnClass={BOARD_TOOLBOX_BASE_BTN_CLASS}
        />
      </BoardToolboxBar>
      {hasContextOptions && (
        <BoardToolboxOverlay rightInset={rightInset}>
          {mapTool === 'measure' && (
            <BoardToolboxSection className='text-xs'>
              <span>Mode mesure</span>
              <BoardToolboxActionButton
                title='Mesure'
                active={measureMode === 'measure'}
                onClick={() => onMeasureModeChange?.('measure')}
              >
                M
              </BoardToolboxActionButton>
              <BoardToolboxActionButton
                title='Reglage'
                active={measureMode === 'settings'}
                onClick={() => onMeasureModeChange?.('settings')}
              >
                R
              </BoardToolboxActionButton>
            </BoardToolboxSection>
          )}
          {showColorPalette && (
            <BoardToolboxColorPalette
              value={selectedColor}
              onChange={onColorChange}
              titlePrefix='Couleur selection'
            />
          )}
          {showArrowOptions && (
            <BoardToolboxSection>
              <BoardToolboxSelect
                minWidthClass='min-w-32'
                options={MAP_ARROW_OPTIONS}
                value={linkArrows}
                onChange={onLinkArrowsChange}
                placeholder='Flèches du trait'
              />
            </BoardToolboxSection>
          )}
          {showIconPicker && (
            <BoardToolboxSection>
              <BoardToolboxSelect
                minWidthClass='min-w-20'
                options={MAP_PIN_SIZE_OPTIONS}
                value={selectedPinSize}
                onChange={onPinSizeChange}
                placeholder='Taille'
              />
              <div className='relative'>
                <BoardToolboxActionButton
                  title='icône du pin'
                  onClick={() => setIconPickerOpen(prev => !prev)}
                >
                  {selectedPinIconSlug ? (
                    <span className='flex items-center justify-center'>
                      {renderIconBySlug(selectedPinIconSlug, { size: 14 })}
                    </span>
                  ) : (
                    <FaIcons size={12} />
                  )}
                </BoardToolboxActionButton>
                {iconPickerOpen && (
                  <div className='absolute top-8 right-0 z-40 w-72 rounded border border-contrast border-opacity-20 bg-main p-1 shadow'>
                    <IconPicker
                      value={selectedPinIconSlug}
                      label={null}
                      size={18}
                      previewSize={18}
                      maxHeight={180}
                      onChange={(label, slug) => {
                        onPinIconChange?.(slug || null);
                      }}
                      onClear={() => onPinIconChange?.(null)}
                    />
                  </div>
                )}
              </div>
            </BoardToolboxSection>
          )}
          {(showLinkItem || showUnlinkItem) && (
            <BoardToolboxSection>
              {showUnlinkItem ? (
                <BoardToolboxActionButton
                  title='retirer l’item lié'
                  onClick={() => onUnlinkItem?.()}
                >
                  <FaLink size={12} />
                  <span className='text-xs ml-05'>×</span>
                </BoardToolboxActionButton>
              ) : (
                <BoardToolboxActionButton
                  title='lier un item'
                  onClick={() => onOpenLinkModal?.()}
                >
                  <FaLink size={12} />
                </BoardToolboxActionButton>
              )}
            </BoardToolboxSection>
          )}
          {showBorderStyleOptions && (
            <BoardToolboxSection>
              <BoardToolboxBorderStyle
                value={selectedBorderStyle}
                onChange={onBorderStyleChange}
                placeholder='Trait'
              />
            </BoardToolboxSection>
          )}
          {(mapTool === 'shape' || showShapeColorOptions) && (
            <BoardToolboxSection>
              <BoardToolboxSelect
                minWidthClass='min-w-24'
                options={MAP_SHAPE_TYPE_OPTIONS}
                value={mapShapeType}
                onChange={onMapShapeTypeChange}
                placeholder='Forme'
              />
              {showShapeLayerOptions && (
                <div className='flex items-center gap-05'>
                  <BoardToolboxActionButton
                    title='Mettre à l’arrière-plan'
                    onClick={() => onLayerChange?.('back')}
                  >
                    ↓
                  </BoardToolboxActionButton>
                  <BoardToolboxActionButton
                    title='Mettre au premier plan'
                    onClick={() => onLayerChange?.('front')}
                  >
                    ↑
                  </BoardToolboxActionButton>
                </div>
              )}
            </BoardToolboxSection>
          )}
        </BoardToolboxOverlay>
      )}
    </>
  );
};
